import logger from "@airhelp/node-logger";
import { Button } from "@chakra-ui/react";
import type { As, ButtonProps } from "@chakra-ui/react";
import { useModal } from "contexts/ModalContext";
import { useStoryblokLink } from "hooks";
import type { ReactStoryblokComponent } from "types/storyblok";
import { trackCustomEvent, trackEcommerceEvent, trackUAClickEvent } from "utils/tracking/utils";
import { modals, sizes, variants } from "./Button.config";
import type { ButtonStoryblokAdapterProps } from "./Button.d";

const ButtonStoryblokAdapter = ({ blok, sbEditable }: ReactStoryblokComponent<ButtonStoryblokAdapterProps>) => {
  const {
    size,
    text,
    variant,
    link,
    modal,
    modalSize = "md",
    gtmSection,
    gtmElement,
    gaLabel,
    gaPlacement,
    isExternal,
    maxWidth,
    ga4ETrackingJSON,
  } = blok;

  const { openModal } = useModal();
  const href = useStoryblokLink(link);

  const trackingEvents = () => {
    trackUAClickEvent(gtmSection, gtmElement);
    trackCustomEvent({ event: "cta_click", label: gaLabel, placement: gaPlacement });

    if (ga4ETrackingJSON) {
      try {
        const { event, ecommerce } = JSON.parse(ga4ETrackingJSON);
        trackEcommerceEvent({ event, ...ecommerce });
      } catch (error) {
        logger.error("Error parsing ga4ETrackingJSON", error);
      }
    }
  };

  const width = maxWidth
    ? { width: "100%", maxWidth: { base: "unset", md: `${maxWidth}px` } }
    : { width: { base: "100%", md: "auto" }, maxWidth: "none" };

  const buttonModalProps = {
    size: sizes[size],
    onClick: () => {
      openModal(modals[modal], modalSize);
      trackingEvents();
    },
    variant: variants[variant],
    minW: "fit-content",
    whiteSpace: "nowrap",
  } as ButtonProps;

  const buttonProps = {
    onClick: trackingEvents,
    variant: variants[variant],
    size: sizes[size],
    as: "a" as As,
    href,
    target: isExternal ? "_blank" : "_self",
    ...width,
  };

  const props = modal ? buttonModalProps : buttonProps;

  return (
    <Button {...props} {...sbEditable}>
      {text}
    </Button>
  );
};

export default ButtonStoryblokAdapter;
